import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import PortalContainer from '@widget/popups/PortalContainer';
import Transition from '@helper/transition/Transition';
import { UXPContext } from '@core/context/Context';
import MobileSheet from '@widget/popups/MobileSheet';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
// Maps each position to its opposite axis counterpart for smart overflow flipping
const POSITION_FLIP_MAP = {
    'bottom': 'top', 'top': 'bottom',
    'bottom-left': 'top-left', 'top-left': 'bottom-left',
    'bottom-right': 'top-right', 'top-right': 'bottom-right',
    'bottom-left-corner': 'top-left-corner', 'top-left-corner': 'bottom-left-corner',
    'bottom-right-corner': 'top-right-corner', 'top-right-corner': 'bottom-right-corner',
    'left': 'right', 'right': 'left',
    'left-top': 'right-top', 'right-top': 'left-top',
    'left-bottom': 'right-bottom', 'right-bottom': 'left-bottom',
};
/**
 * @export
 * A component that displays a dropdown menu with customizable positioning and animation.
 *
 * ## USAGE PATTERNS
 *
 * ### 1. LEGACY MODE (Wrapped Trigger) - Backward Compatible
 * The dropdown wraps and manages the trigger element. Use for simple cases and backwards compatibility.
 * The dropdown handles all click logic automatically.
 *
 * ```tsx
 * <Dropdown
 *   trigger={<button>Click me</button>}
 *   content={<div>Dropdown content</div>}
 * />
 * ```
 *
 * **With triggerElementRef for precise click detection:**
 * ```tsx
 * const buttonRef = useRef<HTMLButtonElement>(null);
 *
 * <Dropdown
 *   trigger={
 *     <div>
 *       <button ref={buttonRef}>Toggle</button>
 *       <span>Other content</span>
 *     </div>
 *   }
 *   triggerElementRef={buttonRef}
 *   content={<div>Only button clicks will toggle</div>}
 * />
 * ```
 *
 * ### 2. EXTERNAL TRIGGER MODE (v5 Pattern) - Recommended for Inputs
 * The trigger is a sibling of the dropdown, giving full styling control.
 * Use for input components requiring base class styling.
 * Parent handles trigger clicks, dropdown handles outside clicks automatically.
 *
 * ```tsx
 * const triggerRef = useRef<HTMLDivElement>(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <div className="uxpcore_input uxp-form-select">
 *   <div
 *     ref={triggerRef}
 *     className="uxpcore_input__trigger"
 *     onClick={() => setIsOpen(!isOpen)}
 *     tabIndex={0}
 *   >
 *     Select an option
 *   </div>
 *
 *   <Dropdown
 *     externalTriggerRef={triggerRef}
 *     content={<div>Dropdown options</div>}
 *     isOpen={isOpen}
 *     onToggle={() => setIsOpen(!isOpen)}
 *     matchTriggerWidth={true}
 *   />
 * </div>
 * ```
 *
 * ## CLICK HANDLING BEHAVIOR
 *
 * ### Legacy Mode
 * - Dropdown wraps trigger and handles all clicks
 * - Clicking trigger → Dropdown's internal handler toggles state
 * - Clicking outside (backdrop) → Dropdown closes via backdrop handler
 * - Can be controlled (isOpen + onToggle) or uncontrolled
 *
 * ### External Trigger Mode
 * - Trigger and dropdown are siblings (not parent-child)
 * - Clicking trigger → Parent's onClick handler toggles state
 * - Clicking outside (backdrop) → Dropdown's onToggle callback → Parent closes
 * - MUST be controlled (isOpen + onToggle required)
 * - **Click isolation guaranteed**: Trigger is in normal DOM, backdrop is in Portal
 *
 * ## CLICK ISOLATION TECHNICAL DETAILS
 *
 * No double-firing occurs because:
 * 1. External trigger element is in normal DOM tree (parent component)
 * 2. Dropdown portal renders to document.body (via PortalContainer)
 * 3. Backdrop is rendered inside the portal
 * 4. Clicking trigger CANNOT trigger backdrop onClick (different DOM trees)
 * 5. Clicking backdrop calls onClickBackdrop → closes dropdown
 *
 * @example Full featured legacy mode
 * ```tsx
 * <Dropdown
 *   trigger={<button>Menu</button>}
 *   content={
 *     <div>
 *       <div>Option 1</div>
 *       <div>Option 2</div>
 *     </div>
 *   }
 *   position="top-right"
 *   duration={300}
 *   isOpen={true}
 *   onToggle={() => console.log('Dropdown toggled')}
 *   showAnchor={true}
 *   className="custom-dropdown"
 * />
 * ```
 *
 * @category Overlays
 */
export const Dropdown = forwardRef(({ content, trigger, triggerElementRef, externalTriggerRef, className, contentClassName, position = 'bottom-left', duration = 200, isOpen: controlledIsOpen, preventOpening, onToggle, showAnchor = false, matchTriggerWidth = false, minWidth, maxWidth, mobileSheet = true, }, ref) => {
    const context = useContext(UXPContext);
    const asSheet = useIsMobileLayout() && mobileSheet;
    const internalTriggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const [isRepositioned, setIsRepositioned] = useState(false);
    const [isPositionReady, setIsPositionReady] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const exitTimerRef = useRef(null);
    // Use external trigger ref if provided, otherwise use internal ref
    const triggerRef = externalTriggerRef || internalTriggerRef;
    const isControlled = controlledIsOpen !== undefined && onToggle !== undefined;
    const isOpen = !preventOpening && (isControlled ? controlledIsOpen : internalOpen);
    const toggleOpen = useCallback(() => {
        if (preventOpening)
            return;
        if (isControlled) {
            onToggle === null || onToggle === void 0 ? void 0 : onToggle();
        }
        else {
            setInternalOpen(true);
        }
    }, [preventOpening, isControlled, onToggle]);
    const close = useCallback(() => {
        if (isControlled) {
            onToggle === null || onToggle === void 0 ? void 0 : onToggle();
        }
        else {
            setInternalOpen(false);
        }
    }, [isControlled, onToggle]);
    // Phase 1: calculate position from trigger rect only (no DOM reads of the dropdown)
    const calculatePosition = useCallback((positionOverride) => {
        if (!triggerRef.current)
            return {};
        const rect = triggerRef.current.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;
        const isRTL = context.direction == 'rtl';
        const gap = 8;
        const effectivePosition = positionOverride !== null && positionOverride !== void 0 ? positionOverride : position;
        let pos = {};
        if (effectivePosition === 'bottom') {
            pos = { top: rect.bottom + gap, left: isRTL ? 'auto' : rect.left + rect.width / 2, right: isRTL ? (innerWidth - rect.right + rect.width / 2) : 'auto', translate: '-50% 0' };
        }
        else if (effectivePosition === 'bottom-left') {
            pos = { top: rect.bottom + gap, left: isRTL ? 'auto' : rect.left, right: isRTL ? (innerWidth - rect.right) : 'auto' };
        }
        else if (effectivePosition === 'bottom-right') {
            pos = { top: rect.bottom + gap, right: isRTL ? 'auto' : (innerWidth - rect.right), left: isRTL ? rect.right : 'auto' };
        }
        else if (effectivePosition === 'top') {
            pos = { bottom: innerHeight - rect.top + gap, left: isRTL ? 'auto' : rect.left + rect.width / 2, right: isRTL ? (innerWidth - rect.right + rect.width / 2) : 'auto', translate: '-50% 0' };
        }
        else if (effectivePosition === 'top-left') {
            pos = { bottom: innerHeight - rect.top + gap, left: isRTL ? 'auto' : rect.left, right: isRTL ? (innerWidth - rect.right) : 'auto' };
        }
        else if (effectivePosition === 'top-right') {
            pos = { bottom: innerHeight - rect.top + gap, right: isRTL ? 'auto' : (innerWidth - rect.right), left: isRTL ? rect.right : 'auto' };
        }
        else if (effectivePosition === 'right') {
            pos = { top: rect.top + rect.height / 2, left: isRTL ? 'auto' : (rect.right + gap), right: isRTL ? (innerWidth - rect.left + gap) : 'auto', translate: '0 -50%' };
        }
        else if (effectivePosition === 'left') {
            pos = { top: rect.top + rect.height / 2, left: isRTL ? (rect.right + gap) : 'auto', right: isRTL ? 'auto' : (innerWidth - rect.left + gap), translate: '0 -50%' };
        }
        else if (effectivePosition === 'right-top') {
            pos = { top: rect.top, left: isRTL ? 'auto' : (rect.right + gap), right: isRTL ? (innerWidth - rect.left + gap) : 'auto' };
        }
        else if (effectivePosition === 'right-bottom') {
            pos = { top: rect.bottom, left: isRTL ? 'auto' : (rect.right + gap), right: isRTL ? (innerWidth - rect.left + gap) : 'auto', translate: '0 -100%' };
        }
        else if (effectivePosition === 'left-top') {
            pos = { top: rect.top, left: isRTL ? (rect.right + gap) : 'auto', right: isRTL ? 'auto' : (innerWidth - rect.left + gap) };
        }
        else if (effectivePosition === 'left-bottom') {
            pos = { top: rect.bottom, left: isRTL ? (rect.right + gap) : 'auto', right: isRTL ? 'auto' : (innerWidth - rect.left + gap), translate: '0 -100%' };
        }
        else if (effectivePosition === 'top-left-corner') {
            pos = { bottom: innerHeight - rect.top, right: isRTL ? 'auto' : (innerWidth - rect.left), left: isRTL ? rect.right : 'auto' };
        }
        else if (effectivePosition === 'top-right-corner') {
            pos = { bottom: innerHeight - rect.top, left: isRTL ? 'auto' : rect.right, right: isRTL ? (innerWidth - rect.left) : 'auto' };
        }
        else if (effectivePosition === 'bottom-left-corner') {
            pos = { top: rect.bottom, right: isRTL ? 'auto' : (innerWidth - rect.left), left: isRTL ? rect.right : 'auto' };
        }
        else if (effectivePosition === 'bottom-right-corner') {
            pos = { top: rect.bottom, left: isRTL ? 'auto' : rect.right, right: isRTL ? (innerWidth - rect.left) : 'auto' };
        }
        if (matchTriggerWidth)
            pos = Object.assign(Object.assign({}, pos), { width: rect.width });
        // Numbers are px at the 13px base font; as rem they grow with the screen like the rest of the UI.
        if (minWidth !== undefined)
            pos = Object.assign(Object.assign({}, pos), { minWidth: typeof minWidth === 'number' ? `${minWidth / 13}rem` : minWidth });
        if (maxWidth !== undefined)
            pos = Object.assign(Object.assign({}, pos), { maxWidth: typeof maxWidth === 'number' ? `${maxWidth / 13}rem` : maxWidth });
        return pos;
    }, [position, context.direction, matchTriggerWidth, minWidth, maxWidth]);
    // Phase 2: read the rendered dropdown rect and apply overflow adjustments with smart axis-flipping
    const applyOverflowAdjustments = useCallback((pos) => {
        var _a, _b, _c, _d;
        if (!dropdownRef.current || !triggerRef.current)
            return pos;
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;
        const gap = 8;
        let finalPos = Object.assign({}, pos);
        let repositioned = false;
        // Track per-axis so a flip on one axis doesn't suppress last-resort clamping on the other.
        let verticalFlipped = false;
        let horizontalFlipped = false;
        // Smart vertical flip: try opposite axis before clamping
        const isBottomPos = position.startsWith('bottom');
        const isTopPos = position.startsWith('top');
        if (isBottomPos && dropdownRect.bottom > innerHeight) {
            const flipped = (_a = POSITION_FLIP_MAP[position]) !== null && _a !== void 0 ? _a : position;
            const spaceAbove = triggerRect.top - gap;
            const spaceBelow = innerHeight - triggerRect.bottom - gap;
            if (spaceAbove >= dropdownRect.height) {
                // Enough room above — flip cleanly
                finalPos = calculatePosition(flipped);
                repositioned = true;
                verticalFlipped = true;
            }
            else if (spaceAbove > spaceBelow) {
                // More space above but still tight — flip and constrain height
                finalPos = Object.assign(Object.assign({}, calculatePosition(flipped)), { maxHeight: `${spaceAbove}px`, overflowY: 'auto' });
                repositioned = true;
                verticalFlipped = true;
            }
        }
        else if (isTopPos && dropdownRect.top < 0) {
            const flipped = (_b = POSITION_FLIP_MAP[position]) !== null && _b !== void 0 ? _b : position;
            const spaceBelow = innerHeight - triggerRect.bottom - gap;
            const spaceAbove = triggerRect.top - gap;
            if (spaceBelow >= dropdownRect.height) {
                finalPos = calculatePosition(flipped);
                repositioned = true;
                verticalFlipped = true;
            }
            else if (spaceBelow > spaceAbove) {
                finalPos = Object.assign(Object.assign({}, calculatePosition(flipped)), { maxHeight: `${spaceBelow}px`, overflowY: 'auto' });
                repositioned = true;
                verticalFlipped = true;
            }
        }
        // Smart horizontal flip: try opposite axis before clamping
        const isLeftPos = position.startsWith('left');
        const isRightPos = position.startsWith('right');
        if (isLeftPos && dropdownRect.left < 0) {
            const flipped = (_c = POSITION_FLIP_MAP[position]) !== null && _c !== void 0 ? _c : position;
            const spaceRight = innerWidth - triggerRect.right - gap;
            if (spaceRight >= dropdownRect.width) {
                finalPos = Object.assign(Object.assign({}, finalPos), calculatePosition(flipped));
                repositioned = true;
                horizontalFlipped = true;
            }
        }
        else if (isRightPos && dropdownRect.right > innerWidth) {
            const flipped = (_d = POSITION_FLIP_MAP[position]) !== null && _d !== void 0 ? _d : position;
            const spaceLeft = triggerRect.left - gap;
            if (spaceLeft >= dropdownRect.width) {
                finalPos = Object.assign(Object.assign({}, finalPos), calculatePosition(flipped));
                repositioned = true;
                horizontalFlipped = true;
            }
        }
        // Last-resort clamping — applied per axis, independent of the other axis' flip.
        const changes = {};
        if (dropdownRect.height >= innerHeight) {
            changes.maxHeight = '80vh';
            repositioned = true;
        }
        if (dropdownRect.width >= innerWidth && !maxWidth) {
            changes.maxWidth = '80vw';
            repositioned = true;
        }
        // Vertical clamp — skip only when a vertical flip already resolved it
        if (!verticalFlipped) {
            if (dropdownRect.top < 0) {
                changes.top = 20;
                changes.bottom = 'auto';
                repositioned = true;
            }
            else if (dropdownRect.bottom > innerHeight) {
                changes.top = 'auto';
                changes.bottom = 20;
                repositioned = true;
            }
        }
        // Horizontal clamp — skip only when a horizontal flip already resolved it
        if (!horizontalFlipped) {
            if (dropdownRect.left < 0) {
                changes.left = 20;
                changes.right = 'auto';
                repositioned = true;
            }
            else if (dropdownRect.right > innerWidth) {
                changes.right = 20;
                changes.left = 'auto';
                repositioned = true;
            }
        }
        if (repositioned)
            changes.translate = 'unset';
        setIsRepositioned(repositioned);
        return Object.assign(Object.assign({}, finalPos), changes);
    }, [maxWidth, calculatePosition, position]);
    useImperativeHandle(ref, () => ({
        open: toggleOpen,
        close: close,
    }));
    useEffect(() => {
        if (isOpen) {
            // Cancel any pending exit unmount
            if (exitTimerRef.current) {
                clearTimeout(exitTimerRef.current);
                exitTimerRef.current = null;
            }
            setShouldRender(true);
            setIsPositionReady(false);
            // Phase 1: set initial position (invisible) so the dropdown is in the DOM with approximate placement
            requestAnimationFrame(() => {
                const pos = calculatePosition();
                setDropdownPosition(pos);
                // Phase 2: after React re-renders and the browser paints phase 1,
                // read the actual rendered rect for overflow adjustment, then reveal + animate
                requestAnimationFrame(() => {
                    setDropdownPosition(applyOverflowAdjustments(calculatePosition()));
                    setIsPositionReady(true);
                });
            });
            const handleResize = () => {
                const pos = calculatePosition();
                setDropdownPosition(applyOverflowAdjustments(pos));
            };
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
        else {
            // Trigger exit animation, then unmount after it completes
            setIsPositionReady(false);
            exitTimerRef.current = setTimeout(() => {
                setShouldRender(false);
            }, duration);
            return () => {
                if (exitTimerRef.current)
                    clearTimeout(exitTimerRef.current);
            };
        }
    }, [isOpen, calculatePosition, applyOverflowAdjustments, duration]);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close();
            }
        };
        // A sheet handles Escape itself
        if (isOpen && !asSheet) {
            document.addEventListener('keydown', handleEscape);
        }
        // Cleanup function
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, toggleOpen]);
    const handleToggle = useCallback((e) => {
        const clickedElement = e.target;
        // If triggerElementRef is provided, verify click is within it
        // This allows precise click detection for complex nested triggers
        // Useful when trigger has multiple clickable elements and you want to control which ones toggle
        if (triggerElementRef === null || triggerElementRef === void 0 ? void 0 : triggerElementRef.current) {
            const isWithinTrigger = clickedElement === triggerElementRef.current ||
                triggerElementRef.current.contains(clickedElement);
            if (!isWithinTrigger) {
                return; // Ignore clicks outside the specified trigger ref
            }
        }
        // Toggle the dropdown
        e.preventDefault();
        e.stopPropagation();
        toggleOpen();
    }, [toggleOpen, triggerElementRef]);
    const handleClose = useCallback(() => {
        if (isControlled) {
            onToggle === null || onToggle === void 0 ? void 0 : onToggle();
        }
        else {
            setInternalOpen(false);
        }
    }, [isControlled, onToggle]);
    const animationClassname = `uxpcore_dropdown__animation_${position}`;
    const anchorClassname = `uxpcore_dropdown__anchor_${position}`;
    // Determine if we're in external trigger mode
    const isExternalTriggerMode = !!externalTriggerRef;
    // Render dropdown content (phones: in a bottom sheet)
    const dropdownContent = asSheet ? (_jsx(MobileSheet, { isOpen: isOpen, onClose: handleClose, children: _jsx("div", { className: classNames("uxpcore_dropdown__content", "uxpcore_dropdown__content--sheet", contentClassName), ref: dropdownRef, children: content }) })) : shouldRender ? (_jsx(PortalContainer, { hasBackdrop: true, onClickBackdrop: handleClose, className: 'uxpcore_dropdown_popup__container', children: _jsx(Transition, { in: isPositionReady, duration: duration, className: animationClassname, unmountOnExit: false, children: _jsx("div", { className: classNames("uxpcore_dropdown__wrapper", { [anchorClassname]: showAnchor && !isRepositioned }), style: dropdownPosition, children: _jsx("div", { className: classNames("uxpcore_dropdown__content", contentClassName), ref: dropdownRef, children: content }) }) }) })) : null;
    // External trigger mode: only render dropdown content
    if (isExternalTriggerMode) {
        return _jsx(_Fragment, { children: dropdownContent });
    }
    // Legacy mode: wrap trigger and dropdown in container
    return (_jsxs("div", { className: classNames("uxpcore_dropdown__container", className), children: [_jsx("div", { ref: internalTriggerRef, className: classNames("uxpcore_dropdown__trigger", { 'uxpcore_dropdown__trigger--active': isOpen }), onClick: handleToggle, children: trigger }), dropdownContent] }));
});
